//React
import React from "react"
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import toast from 'react-hot-toast';

// Styles
import "../../styles/dashboard.css";
import "../../styles/modern-theme.css";
import "../../styles/credit-card.css";

// App
import DashLayout from "../../layouts/dashboard/dashLayout";
import { Routes } from "../../routes";
import { Offcanvas, CreditHistoryModal, PlanChangeModal, PlanHistoryModal, PaymentMethodsModal, InvoicesModal, SubscriptionManagementModal, UnverifiedUsersModal } from "../modal";
import TopUpModal from "../modal/TopUpModal";
import { logout, useAppSelector } from "../../stores";
import { FamlaServices } from "../../services";
import { Spiner, CreditCard, CreditActionsPanel } from "../../components";
import { ModernStatCard, ModernButton } from "../../components/modern";
import CreditsService from "../../services/api/creditsService";
import StripeService from "../../services/api/stripeService";
import { getBrandingColor, getTenantInitial, getTenantName } from "../../utils/brandingStyles";

//Chart
import { ChartDashboard } from "../../chart";

//interface
import { iAlert } from "../../interfaces";
import { useTranslation } from "react-i18next";
import { getDashboardCardsData, useCountdown } from "../../utils";
import { Tutorial, FloatingTutorialButton } from "../../components";
import { useTutorial } from "../../hooks/useTutorial";
import { adminTutorialSteps } from "../../config/tutorialSteps";
import { useTheme } from "../../contexts/ThemeContext";
import { useBillingRecommendation } from "../../hooks/useBillingRecommendation";

type BarChartDataset = {
    label: any;
    data: number[];
    backgroundColor: string;
};

type BarChartData = {
    labels: string[];
    datasets: BarChartDataset[];
};

const ModernDashboardView = () => {
    const auth = useAppSelector(state => state.auth);
    const companyId = auth.session.company_id;
    const company = useAppSelector((state) => state.company.company);
    const { t } = useTranslation();
    const { theme } = useTheme();
    const isLightMode = theme === 'light';
    const { isMicrosoftTenant, isMicrosoftRecommended, marketplaceUrl, openMarketplace } = useBillingRecommendation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = React.useState(false)
    const [showCreditHistory, setShowCreditHistory] = React.useState(false)
    const [showTopUpModal, setShowTopUpModal] = React.useState(false)
    const [showPlanChange, setShowPlanChange] = React.useState(false)
    const [showPlanHistory, setShowPlanHistory] = React.useState(false)
    const [showPaymentMethods, setShowPaymentMethods] = React.useState(false)
    const [showInvoices, setShowInvoices] = React.useState(false)
    const [showSubscriptionManagement, setShowSubscriptionManagement] = React.useState(false)
    const [showUnverifiedUsers, setShowUnverifiedUsers] = React.useState(false)
    const [portalLoading, setPortalLoading] = React.useState(false)
    const [systemConfig, setSystemConfig] = React.useState<any>(null)
    const [state, setState] = React.useState({
        totalUsers: 0,
        totalRegisteredUsers: 0,
        unverifiedCount: 0,
        plan: 'FREE',
        accountStatus: 'ACTIVE',
        creditsAvailable: 0,
        creditsQuota: 0,
        creditsConsumedMonth: 0,
        rolloverAvailable: 0,
        rolloverNextMonth: 0,
        monthlyCreditsQuota: 0,
        freeCreditsRemaining: 0,
        initialFreeCredits: 0,
        purchasedCreditsRemaining: 0,
        essentialPackBalance: 0,
        carriedPurchasedCredits: 0,
        giftCreditsRemaining: 0,
        isInGracePeriod: false,
    });

    const cardsData = getDashboardCardsData(state, t, true);

    const [alert, setAlert] = React.useState<iAlert>({
        isActive: false,
        type: '',
        message: ''
    });
    
    let daysRemaing = useCountdown(company?.expired_at)

    const tutorialSteps = adminTutorialSteps(t);
    const {
        run: runTutorial,
        stepIndex,
        steps,
        startTutorial,
        handleJoyrideCallback,
        isTutorialCompleted,
        resetTutorialProgress
    } = useTutorial(tutorialSteps, 'admin', true);

    const [barChartData, setBarChartData] = React.useState<BarChartData>({
        labels: [],
        datasets: [
            {
                label: "Credits AI",
                data: [],
                backgroundColor: getBrandingColor(),
            },
        ],
    });

    // Détecter le retour d'achat Microsoft
    React.useEffect(() => {
        if (searchParams.get('purchase') === 'success' && searchParams.get('source') === 'microsoft') {
            const credits = searchParams.get('credits');
            toast.success(`🎉 ${t('microsoft.purchaseSuccessToast', { credits: credits ?? '' })}`, {
                duration: 5000,
                position: 'top-center'
            });
            
            // Nettoyer l'URL
            setSearchParams({});
        }
    }, [searchParams, setSearchParams, t]);

    React.useEffect(() => {
        async function loadAiCredits() {
            setLoading(true);

            try {
                const response = await CreditsService.getCreditBalance(companyId, auth.access_token);
                
                if (response.success && response.data) {
                    setState(prevState => ({
                        ...prevState,
                        plan: response.data.plan || 'FREE',
                        accountStatus: response.data.status || 'ACTIVE',
                        creditsAvailable: response.data.credits_available || 0,
                        creditsQuota: response.data.credits_quota || 0,
                        creditsConsumedMonth: response.data.credits_consumed_month || 0,
                        rolloverAvailable: response.data.rollover_available || 0,
                        rolloverNextMonth: response.data.rollover_next_month || 0,
                        monthlyCreditsQuota: response.data.monthly_credits_quota || 0,
                        freeCreditsRemaining: response.data.free_credits_remaining || 0,
                        initialFreeCredits: response.data.initial_free_credits || 0,
                        purchasedCreditsRemaining: response.data.purchased_credits_remaining || 0,
                        essentialPackBalance: response.data.essential_pack_balance || 0,
                        carriedPurchasedCredits: response.data.carried_purchased_credits || 0,
                        giftCreditsRemaining: response.data.gift_credits_remaining || 0,
                        isInGracePeriod: response.data.is_in_grace_period || false,
                    }));

                    if ((response.data.plan === 'ENTERPRISE' || response.data.plan === 'FREE') && response.data.credits_available <= 0) {
                        const planName = response.data.plan;
                        setAlert({
                            isActive: true,
                            type: 'warning',
                            message: planName === 'ENTERPRISE' 
                                ? (t('dashboard.enterpriseExpired') || 'Your ENTERPRISE plan has expired (credits exhausted). Please add credits to continue.')
                                : (t('dashboard.freeExpired') || 'Your FREE plan has expired (credits exhausted). Please add credits to continue.')
                        });
                    }
                } else if (response.error === "Unauthorized") {
                    setAlert({ isActive: true, type: "warning", message: 'You do not have authorisation to continue' });
                } else {
                    setState(prevState => ({
                        ...prevState,
                        plan: 'FREE',
                        accountStatus: 'ACTIVE',
                        creditsAvailable: 0,
                        creditsQuota: 0,
                        creditsConsumedMonth: 0,
                        rolloverAvailable: 0,
                        rolloverNextMonth: 0,
                        monthlyCreditsQuota: 0,
                        freeCreditsRemaining: 0,
                        initialFreeCredits: 0,
                        purchasedCreditsRemaining: 0,
                        giftCreditsRemaining: 0,
                        isInGracePeriod: false,
                    }));
                }
            } catch (e) {
                console.error('[Dashboard Client] Load AI Credits error:', e);
                setState(prevState => ({
                    ...prevState,
                    plan: 'FREE',
                    accountStatus: 'ACTIVE',
                    creditsAvailable: 0,
                    creditsQuota: 0,
                    creditsConsumedMonth: 0,
                    rolloverAvailable: 0,
                    rolloverNextMonth: 0,
                    monthlyCreditsQuota: 0,
                    freeCreditsRemaining: 0,
                    initialFreeCredits: 0,
                    purchasedCreditsRemaining: 0,
                    giftCreditsRemaining: 0,
                    isInGracePeriod: false,
                }));
            } finally {
                setLoading(false);
            }
        }
        
        loadAiCredits();
        
        async function loadSystemConfig() {
            try {
                const response = await CreditsService.getSystemConfig(auth.access_token);
                if (response.success && response.data) {
                    setSystemConfig(response.data);
                }
            } catch (error) {
                console.error('[Dashboard] Failed to load system config:', error);
            }
        }
        loadSystemConfig();

        async function loadAllUsers() {
            setLoading(true);

            try {
                let data = await FamlaServices("api_host", `/users/company/${companyId}?page=${1}&limit=${1000000}`, "GET", "", auth.access_token);

                if (data.status === 200) {
                    const totalUser = data.body.data

                    setState(prevState => ({
                        ...prevState,
                        totalUsers: totalUser.total_count ?? (totalUser.count + totalUser.unverified_count),
                        totalRegisteredUsers: totalUser.count,
                        unverifiedCount: totalUser.unverified_count
                    }));
                    setLoading(false)
                }
                else if (data.status === 401 && data.body.description === "You do not have authorisation to continue") {
                    setAlert({ isActive: true, type: "warning", message: 'You do not have authorisation to continue' })
                }
                else if (data.status === 401) {
                    logout();
                }
            }
            catch (e) {
                if (e instanceof Error) {
                    console.error(e.message);
                }
            } finally {
                setLoading(false);
            }
        }
        loadAllUsers();

        async function loadCreditsForTenMonths() {
            setLoading(true);

            try {
                const response = await FamlaServices(
                    "api_host",
                    `/credits/history/monthly/${companyId}`,
                    "GET",
                    "",
                    auth.access_token
                );

                if (response.status === 200 && response.body?.data) {
                    const monthlyData = response.body.data;
                    
                    const labels = monthlyData.map((item: any) => item.month);
                    const datasetData = monthlyData.map((item: any) => item.total);

                    setBarChartData({
                        labels: labels,
                        datasets: [
                            {
                                label: "Crédits AI",
                                data: datasetData,
                                backgroundColor: getBrandingColor(),
                            },
                        ],
                    });
                } else if (response.status === 401) {
                    setAlert({ isActive: true, type: "warning", message: 'You do not have authorisation to continue' });
                } else {
                    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const currentMonth = new Date().getMonth();
                    const labels: string[] = [];
                    for (let i = 10; i >= 0; i--) {
                        labels.push(monthNames[(currentMonth - i + 12) % 12]);
                    }
                    setBarChartData({
                        labels: labels,
                        datasets: [{ label: "Crédits AI", data: new Array(11).fill(0), backgroundColor: getBrandingColor() }],
                    });
                }
            } catch (e) {
                console.error('[Dashboard] Load Credits History error:', e);
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const currentMonth = new Date().getMonth();
                const labels: string[] = [];
                for (let i = 10; i >= 0; i--) {
                    labels.push(monthNames[(currentMonth - i + 12) % 12]);
                }
                setBarChartData({
                    labels: labels,
                    datasets: [{ label: "Crédits AI", data: new Array(11).fill(0), backgroundColor: getBrandingColor() }],
                });
            } finally {
                setLoading(false);
            }
        }

        loadCreditsForTenMonths();

    }, [auth.access_token, companyId]);

    const handleTopUp = () => {
        if (systemConfig) {
            setShowTopUpModal(true);
        } else {
            setAlert({
                isActive: true,
                type: 'warning',
                message: 'Configuration système non disponible. Veuillez réessayer.'
            });
        }
    };

    const handleTopUpPayment = async (topupCredits: number) => {
        try {
            if (isMicrosoftTenant) {
                if (marketplaceUrl) {
                    openMarketplace();
                } else {
                    setAlert({
                        isActive: true,
                        type: 'info',
                        message: t(
                            'dashboard.manageSubscriptionMicrosoftNoUrl',
                            'Votre abonnement est géré via Azure Marketplace. Contactez votre administrateur Azure ou le support Famla.'
                        ),
                    });
                }
                setShowTopUpModal(false);
                return;
            }

            setPortalLoading(true);
            
            const baseUrl = window.location.origin;
            const successUrl = StripeService.generateSuccessUrl(baseUrl, 'ESSENTIAL', true);
            const cancelUrl = StripeService.generateCancelUrl(baseUrl);
            
            const response = await StripeService.createCheckoutSession(
                null,
                companyId,
                topupCredits,
                successUrl,
                cancelUrl,
                auth.access_token
            );

            if (response.success && response.data) {
                window.location.href = response.data.session_url;
            } else {
                const errorMessage = StripeService.formatErrorMessage(response);
                setAlert({
                    isActive: true,
                    type: 'danger',
                    message: `Erreur lors de la création du top-up: ${errorMessage}`
                });
            }
        } catch (error) {
            console.error('[Dashboard] Top-up payment error:', error);
            setAlert({
                isActive: true,
                type: 'danger',
                message: 'Erreur lors du top-up. Veuillez réessayer.'
            });
        } finally {
            setPortalLoading(false);
        }
    };

    const options = React.useMemo(() => {
        const axisColor = isLightMode ? '#4B5563' : '#E5E7EB';
        const gridColor = isLightMode ? 'rgba(17, 24, 39, 0.14)' : 'rgba(255, 255, 255, 0.14)';

        return {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    grid: {
                        display: true,
                        color: gridColor,
                    },
                    ticks: {
                        color: axisColor,
                    },
                },
                y: {
                    beginAtZero: true,
                    grid: {
                        display: true,
                        color: gridColor,
                    },
                    ticks: {
                        color: axisColor,
                    },
                },
            },
            plugins: {
                legend: {
                    display: true,
                    labels: {
                        color: axisColor,
                    },
                },
            },
        };
    }, [isLightMode]);

    return (
        <DashLayout>
            {loading ?
                <Spiner className="text-warning spinner-border-sm" classParent="d-flex justify-content-center align-items-center mt-5" />
                :
                <div className="modern-shell">
                    {/* Modern Header */}
                    <div className="modern-header">
                        <div className="modern-header-left">
                            <NavLink to={Routes.HOMEVIEW} className="modern-logo">
                                <div className="modern-logo-mark">{getTenantInitial()}</div>
                                <span className="modern-logo-text">{getTenantName()}</span>
                            </NavLink>
                        </div>
                        
                        <div className="modern-header-center">
                            <div className="modern-search-box">
                                <svg viewBox="0 0 16 16" fill="none">
                                    <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.3"/>
                                    <path d="M10 10L13.5 13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                                </svg>
                                {t('dashboard.search')}
                                <span className="modern-search-kbd">⌘K</span>
                            </div>
                        </div>
                        
                        <div className="modern-header-right">
                            <NavLink to={Routes.HOMEVIEW} className="modern-btn">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {t("dashboard.exitDashboard")}
                            </NavLink>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="container-fluid p-4 tutorial-admin-welcome" style={{ background: 'var(--bg-primary)' }}>
                        <div className="mb-4">
                            <h1 className="mb-0 fw-bold h3" style={{ color: 'var(--text-primary)' }}>
                                {t('dashboard.dashboard')}
                            </h1>
                        </div>

                        {/* Modern Stats Grid */}
                        <div className="modern-stats-grid" data-tutorial="admin-overview">
                            {cardsData.map((card, index) => (
                                <ModernStatCard
                                    key={index}
                                    label={card.title}
                                    value={card.value}
                                    change={index === 0 ? '+3 ce mois' : index === 1 ? '+12 ce mois' : undefined}
                                    onClick={card.title === t('dashboard.unverifiedUsers') ? () => setShowUnverifiedUsers(true) : undefined}
                                />
                            ))}
                        </div>

                        <div className="row">
                            <div className="col-xl-8 col-lg-12 col-md-12 col-12" data-tutorial="admin-charts">
                                <div className="modern-card" style={{ padding: '0' }}>
                                    <div className="card-header align-items-center d-flex" style={{ 
                                        background: 'var(--bg-secondary)', 
                                        borderBottom: '1px solid var(--border-subtle)',
                                        padding: '16px 20px'
                                    }}>
                                        <h4 className="mb-0" style={{ color: 'var(--text-primary)', fontSize: '19px', fontWeight: '600' }}>
                                            {t('dashboard.monthlyCreditConsumption')}
                                        </h4>
                                    </div>
                                    <div className="card-body" style={{ background: 'var(--bg-secondary)', padding: '20px' }}>
                                        <div style={{ height: '400px' }}>
                                            <ChartDashboard type="bar" data={barChartData} options={options} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-xl-4 col-lg-12 col-md-12 col-12">
                                <div data-tutorial="admin-credits">
                                    <CreditCard
                                        plan={state.plan}
                                        creditsAvailable={state.creditsAvailable}
                                        creditsQuota={state.creditsQuota}
                                        creditsConsumedMonth={state.creditsConsumedMonth}
                                        rolloverAvailable={state.rolloverAvailable}
                                        rolloverNextMonth={state.rolloverNextMonth}
                                        monthlyCreditsQuota={state.monthlyCreditsQuota}
                                        freeCreditsRemaining={state.freeCreditsRemaining}
                                        initialFreeCredits={state.initialFreeCredits}
                                        purchasedCreditsRemaining={state.purchasedCreditsRemaining}
                                        essentialPackBalance={state.essentialPackBalance}
                                        carriedPurchasedCredits={state.carriedPurchasedCredits}
                                        giftCreditsRemaining={state.giftCreditsRemaining}
                                        accountStatus={state.accountStatus}
                                        isInGracePeriod={state.isInGracePeriod}
                                    />
                                </div>

                                <div data-tutorial="admin-actions">
                                    <CreditActionsPanel
                                        plan={state.plan}
                                        creditsAvailable={state.creditsAvailable}
                                        companyHasStripeCustomer={!!company?.stripe_customer_id}
                                        useMicrosoftMarketplace={isMicrosoftTenant}
                                        onManageSubscription={() => {
                                            if (isMicrosoftTenant) {
                                                if (marketplaceUrl) {
                                                    openMarketplace();
                                                } else {
                                                    setAlert({
                                                        isActive: true,
                                                        type: 'info',
                                                        message: t(
                                                            'dashboard.manageSubscriptionMicrosoftNoUrl',
                                                            'Votre abonnement est géré via Azure Marketplace. Contactez votre administrateur Azure ou le support Famla.'
                                                        ),
                                                    });
                                                }
                                                return;
                                            }
                                            setShowSubscriptionManagement(true);
                                        }}
                                        onTopUp={handleTopUp}
                                        onViewHistory={() => setShowCreditHistory(true)}
                                        onPlanHistory={() => setShowPlanHistory(true)}
                                        onViewInvoices={() => setShowInvoices(true)}
                                        onViewPaymentMethods={() => setShowPaymentMethods(true)}
                                        loading={portalLoading}
                                    />
                                </div>
                            </div>
                        </div>

                        <Offcanvas />
                        
                        {showCreditHistory && (
                            <CreditHistoryModal
                                id="creditHistoryModal"
                                companyId={companyId}
                                accessToken={auth.access_token}
                                onClose={() => setShowCreditHistory(false)}
                            />
                        )}

                        {showTopUpModal && systemConfig && (
                            <TopUpModal
                                id="topUpModal"
                                config={systemConfig}
                                currentCredits={state.creditsAvailable}
                                onClose={() => setShowTopUpModal(false)}
                                onTopUpRequest={handleTopUpPayment}
                                useMicrosoftMarketplace={isMicrosoftTenant}
                                marketplaceUrl={marketplaceUrl}
                            />
                        )}

                        {showPlanChange && (
                            <PlanChangeModal
                                id="planChangeModal"
                                currentPlan={state.plan}
                                companyId={companyId}
                                accessToken={auth.access_token}
                                onClose={() => setShowPlanChange(false)}
                                onSuccess={() => {
                                    setShowPlanChange(false);
                                    window.location.reload();
                                }}
                            />
                        )}

                        {showPlanHistory && (
                            <PlanHistoryModal
                                id="planHistoryModal"
                                companyId={companyId}
                                accessToken={auth.access_token}
                                onClose={() => setShowPlanHistory(false)}
                            />
                        )}

                        {showPaymentMethods && (
                            <PaymentMethodsModal
                                id="paymentMethodsModal"
                                companyId={companyId}
                                accessToken={auth.access_token}
                                onClose={() => setShowPaymentMethods(false)}
                            />
                        )}

                        {showInvoices && (
                            <InvoicesModal
                                id="invoicesModal"
                                companyId={companyId}
                                accessToken={auth.access_token}
                                onClose={() => setShowInvoices(false)}
                            />
                        )}

                        {showSubscriptionManagement && (
                            <div data-tutorial="admin-subscription">
                                <SubscriptionManagementModal
                                    id="subscriptionManagementModal"
                                    companyId={companyId}
                                    accessToken={auth.access_token}
                                    onClose={() => setShowSubscriptionManagement(false)}
                                    onSuccess={() => {
                                        setShowSubscriptionManagement(false);
                                        window.location.reload();
                                    }}
                                />
                            </div>
                        )}

                        {showUnverifiedUsers && (
                            <UnverifiedUsersModal
                                id="unverifiedUsersModal"
                                companyId={companyId}
                                accessToken={auth.access_token}
                                onClose={() => setShowUnverifiedUsers(false)}
                            />
                        )}

                        <Tutorial
                            run={runTutorial}
                            steps={steps}
                            stepIndex={stepIndex}
                            callback={handleJoyrideCallback}
                            continuous={true}
                            showProgress={true}
                            showSkipButton={true}
                        />

                        <FloatingTutorialButton onClick={startTutorial} />
                    </div>
                </div>
            }
        </DashLayout>
    )
}

export default ModernDashboardView;
